import React, { useEffect, useState } from 'react';
import { getWithdrawalRecommendation, getWithdrawalsHistory, logWithdrawal } from '../api';
import { DollarSign, TrendingUp, Calendar, ArrowDownCircle } from 'lucide-react';

const TIERS = [
  { name: 'Build',    range: '$0 - $25K',     floor: '$10K', pct: '0%',  cadence: 'None' },
  { name: 'Bank',     range: '$25K - $50K',   floor: '$20K', pct: '25%', cadence: 'Bi-weekly' },
  { name: 'Scale',    range: '$50K - $100K',  floor: '$35K', pct: '35%', cadence: 'Bi-weekly' },
  { name: 'Harvest',  range: '$100K - $250K', floor: '$60K', pct: '50%', cadence: 'Weekly' },
  { name: 'Mature',   range: '$250K - $500K', floor: '$125K', pct: '60%', cadence: 'Weekly' },
  { name: 'Wealth',   range: '$500K+',        floor: '$250K', pct: '70%', cadence: 'Weekly' },
];

export default function ProfitTaking() {
  const [weekly, setWeekly] = useState<any>(null);
  const [biweekly, setBiweekly] = useState<any>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [w, bw, h] = await Promise.all([
          getWithdrawalRecommendation('weekly'),
          getWithdrawalRecommendation('biweekly'),
          getWithdrawalsHistory()
        ]);
        setWeekly(w.data);
        setBiweekly(bw.data);
        setHistory(h.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLog = async (rec: any) => {
    if (!rec || rec.recommended_amount <= 0) return;
    if (!window.confirm(`Log withdrawal of $${rec.recommended_amount}?`)) return;
    await logWithdrawal(rec.recommended_amount, rec.period, rec.reason);
    const h = await getWithdrawalsHistory();
    setHistory(h.data || []);
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-500">Loading...</div>;

  const balance = weekly?.balance || 0;
  const startingBalance = weekly?.starting_balance || 0;
  const balancePositive = startingBalance > 0 ? balance >= startingBalance : balance > 0;
  const currentTier = weekly?.tier || 'Build';
  const accountName = weekly?.account_name;
  const env = weekly?.env;
  const source = weekly?.source;
  const isOffline = source === 'fallback';
  const showPaper = source === 'internal' || (source === 'tradovate' && env === 'demo');
  const totalWithdrawn = history.filter(h => h.status === 'completed').reduce((s, h) => s + (h.amount || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div><h1 className="text-xl font-bold">Profit</h1><p className="text-xs text-gray-500 mt-0.5">Profit taking configuration. STDV-based target levels and partial exit rules for active positions.</p></div>
        {showPaper && (
          <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded bg-gray-800 text-gray-400 border border-gray-700">
            PAPER
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-gray-900 rounded-lg border border-gray-800 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500 uppercase tracking-wide">Account Balance</span>
            <DollarSign size={14} className="text-gray-600" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-2xl font-bold font-mono ${balancePositive ? 'text-green-400' : 'text-red-400'}`}>
              ${balance.toFixed(2)}
            </span>
            {isOffline && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-yellow-900 text-yellow-300 border border-yellow-800">
                Offline - internal calc
              </span>
            )}
          </div>
          {(accountName || env) && (
            <div className="text-xs text-gray-500 mt-1 font-mono">
              {accountName}{accountName && env ? ' · ' : ''}{env?.toUpperCase()}
            </div>
          )}
        </div>
        <Stat label="Current Tier" value={currentTier} icon={TrendingUp} />
        <Stat label="Floor" value={`$${weekly?.floor?.toLocaleString() || '0'}`} icon={ArrowDownCircle} />
        <Stat label="Total Withdrawn" value={`$${totalWithdrawn.toFixed(2)}`} icon={Calendar} positive />
      </div>

      <div>
        <Recommendation
          title={`${(weekly?.tier === 'Build' ? 'No' : (weekly?.tier === 'Bank' || weekly?.tier === 'Scale' ? 'Bi-weekly' : 'Weekly'))} Recommendation`}
          rec={(weekly?.tier === 'Bank' || weekly?.tier === 'Scale') ? biweekly : weekly}
          onLog={handleLog}
        />
      </div>

      <div className="bg-gray-900 rounded-lg border border-gray-800 p-4">
        <h2 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wide">Tier Framework</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                <th className="text-left p-2">Tier</th>
                <th className="text-left p-2">Account Range</th>
                <th className="text-left p-2">Floor</th>
                <th className="text-left p-2">Withdraw %</th>
                <th className="text-left p-2">Schedule</th>
              </tr>
            </thead>
            <tbody>
              {TIERS.map((t) => (
                <tr key={t.name} className={`border-b border-gray-800 ${t.name === currentTier ? 'bg-blue-900 bg-opacity-20' : ''}`}>
                  <td className="p-2 font-bold">{t.name} {t.name === currentTier && <span className="text-xs text-blue-400 ml-1">← current</span>}</td>
                  <td className="p-2 font-mono">{t.range}</td>
                  <td className="p-2 font-mono">{t.floor}</td>
                  <td className="p-2 font-mono">{t.pct}</td>
                  <td className="p-2">{t.cadence}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-gray-900 rounded-lg border border-gray-800">
        <div className="p-4 border-b border-gray-800">
          <h2 className="font-semibold">Withdrawal History</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Amount</th>
                <th className="text-left p-3">Period</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Notes</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-gray-600 p-8">No withdrawals yet</td></tr>
              ) : history.map((h: any) => (
                <tr key={h.id} className="border-b border-gray-800 hover:bg-gray-800">
                  <td className="p-3 text-gray-400 text-xs">{new Date(h.created_at).toLocaleString()}</td>
                  <td className="p-3 font-mono font-bold text-green-400">${h.amount?.toFixed(2)}</td>
                  <td className="p-3">{h.period}</td>
                  <td className="p-3"><span className={`text-xs px-1.5 py-0.5 rounded ${h.status === 'completed' ? 'bg-green-900 text-green-400' : 'bg-yellow-900 text-yellow-400'}`}>{h.status}</span></td>
                  <td className="p-3 text-xs text-gray-500">{h.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, positive }: any) {
  return (
    <div className="bg-gray-900 rounded-lg border border-gray-800 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
        <Icon size={14} className="text-gray-600" />
      </div>
      <span className={`text-2xl font-bold font-mono ${positive ? 'text-green-400' : 'text-white'}`}>{value}</span>
    </div>
  );
}

function Recommendation({ title, rec, onLog }: any) {
  if (!rec) return null;
  const canWithdraw = rec.action === 'withdraw' && rec.recommended_amount > 0;
  return (
    <div className="bg-gray-900 rounded-lg border border-gray-800 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide">{title}</h3>
        {rec.next_action_date && <span className="text-xs text-gray-500">Next: {new Date(rec.next_action_date + 'T16:30:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>}
      </div>
      <div className="space-y-2">
        <div className="flex justify-between"><span className="text-gray-500">Period P&L:</span><span className={`font-mono font-bold ${rec.period_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>${rec.period_pnl?.toFixed(2)}</span></div>
        <div className="flex justify-between"><span className="text-gray-500">Recommended:</span><span className={`font-mono font-bold text-2xl ${canWithdraw ? 'text-green-400' : 'text-gray-600'}`}>${rec.recommended_amount?.toFixed(2)}</span></div>
        <div className="text-xs text-gray-500 mt-2">{rec.reason}</div>
        {canWithdraw && (
          <button onClick={() => onLog(rec)} className="w-full mt-3 bg-green-700 hover:bg-green-600 text-white py-2 rounded font-bold text-sm">
            Log This Withdrawal
          </button>
        )}
      </div>
    </div>
  );
}
